import { React, useEffect } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { Link } from 'react-router-dom';

import './visual-debug.css';
import '../components.css';

function ScrollToTopOnMount() {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return null;
}

export function VisualDebugging() {
    return (
        <section id="debugging" className="components">
            <ScrollToTopOnMount />

            <div className="return">
                <Link to="/">[ Return ]</Link>
            </div>

            <Container fluid className="logos" style={{ marginTop: '50px' }}>
                <Row className="align-items-center" sm={12}>
                    <Col sm={2}>
                        <img className="logo" src="images/logos/dbvis-logo.png" alt="DBVIS Logo" />
                        <img className="logo" src="images/logos/Siemens.png" alt="Siemens Logo" />
                    </Col>
                    <Col></Col>
                    <Col sm={2}>
                        <img className="logo" src="images/logos/unikn-logo.png" alt="UNI KN Logo" />
                        <img
                            className="logo"
                            src="images/logos/Technical-University-Munich.png"
                            alt="TU Munich Logo"
                        />
                    </Col>
                </Row>
            </Container>
            <Container fluid>
                <Row className="align-items-center" sm={12}>
                    <Col sm={1}></Col>
                    <Col>
                        <h1>
                            Towards Visual Debugging for Multi-Target Time Series Classification
                        </h1>
                    </Col>
                    <Col sm={1}></Col>
                </Row>
                <Row className="align-items-center" sm={12}>
                    <Col sm={2}></Col>
                    <Col>
                        <h3>
                            Udo Schlegel<sup>1</sup>, Eren Cakmak<sup>1</sup>, Hiba Arnout
                            <sup>2,3</sup>, Mennatallah El-Assady
                            <sup>1</sup>, Daniela Oelke<sup>2</sup>, Daniel A. Keim
                            <sup>1</sup>
                        </h3>
                        <h3>
                            <sup>1</sup> University of Konstanz{' '}
                            <span className="text-divider"></span>
                            <sup>2</sup> Siemens CT Munich
                            <span className="text-divider"></span>
                            <sup>3</sup> Technical University Munich
                        </h3>
                    </Col>
                    <Col sm={2}></Col>
                </Row>
                <Row className="divider"></Row>
                <Row className="align-items-center" sm={12}>
                    <Col sm={2}></Col>
                    <Col className="text-align-center" sm={8}>
                        <a href="https://iui.acm.org/2020/program.html">
                            [ ACM Conference on Intelligent User Interfaces (IUI 2020), Cagliari,
                            Italy ]
                        </a>
                    </Col>
                    <Col sm={2}></Col>
                </Row>
                <Row className="divider"></Row>
                <Row className="align-items-center" sm={12}>
                    <Col sm={2}></Col>
                    <Col className="text-align-center">
                        <a href="https://bib.dbvis.de/uploadedFiles/IUI_2020__Towards_Visual_Debugging_for_Multi_Label_Time_SeriesClassification.pdf">
                            [ Paper ]
                        </a>
                    </Col>
                    <Col className="text-align-center">
                        <a href="https://dl.acm.org/doi/abs/10.1145/3377325.3377528">[ ACM DL ]</a>
                    </Col>
                    <Col sm={2}></Col>
                </Row>
                <Row className="divider"></Row>
                <Row className="align-items-center" sm={12}>
                    <Col sm={1}></Col>
                    <Col>
                        <p>
                            <span className="bold">
                                Multi-target classification of multivariate time series data
                            </span>{' '}
                            poses a challenge in many real-world applications, e.g.,{' '}
                            <span className="bold">predictive maintenance</span>. Machine learning
                            methods such as random forests and neural networks support training these
                            classifiers. However, debugging and analyzing possible misclassifications
                            remains challenging due to the{' '}
                            <span className="bold">
                                often complex relations between targets, classes, and the
                                multivariate time series data
                            </span>
                            .
                        </p>
                        <p>
                            We propose a <span className="bold">model-agnostic visual debugging
                            workflow</span> for multi-target time series classification that enables
                            the examination of relations between targets,{' '}
                            <span className="bold">partially correct predictions</span>, potential
                            confusions, and the classified time series data. The workflow, as well as
                            the prototype, aim to foster an in-depth analysis of multi-target
                            classification results to visually identify potential causes of
                            mispredictions.
                        </p>
                    </Col>
                    <Col>
                        <img
                            className="images-text"
                            src="images/2020-debugging/workflow_targets.png"
                            alt="Multi-Target Visual Debugging Workflow"
                        />
                    </Col>
                    <Col sm={1}></Col>
                </Row>
                <Row className="divider"></Row>
                <Row className="align-items-center" sm={12}>
                    <Col sm={1}></Col>
                    <Col>
                        <h2>A Top-Down Visual Debugging Workflow</h2>
                    </Col>
                    <Col sm={1}></Col>
                </Row>
                <Row className="divider"></Row>
                <Row className="align-items-center" sm={12}>
                    <Col sm={1}></Col>
                    <Col>
                        <p>
                            Based on ongoing collaborations with domain experts in predictive
                            maintenance, we collect four{' '}
                            <span className="bold">design goals</span>: the workflow should (1) apply
                            to <span className="bold">any multi-target classifier</span>{' '}
                            model-agnostically, (2) support analysis of{' '}
                            <span className="bold">relationships between multiple targets and
                            classes</span>, (3) visualize the classification results and descriptive
                            representations for a{' '}
                            <span className="bold">single target</span> to compare correct and
                            misclassified samples, and (4) support{' '}
                            <span className="bold">
                                investigation and comparison of the raw multivariate time series
                                data
                            </span>
                            .
                        </p>
                        <p>
                            Following the{' '}
                            <span className="bold">Visual Information Seeking Mantra</span> of
                            overview first, filter, and details on demand, the interactive workflow
                            starts with the{' '}
                            <span className="bold">selection of a trained model</span> (Fig. 1 (1)).
                            An overview then depicts the relationships between the multiple targets
                            and their underlying classes, including{' '}
                            <span className="bold">partially correct results</span> and critical
                            class confusions. Selecting a combination of classes filters down to a
                            single target and its{' '}
                            <span className="bold">confusion matrix</span> (Fig. 1 (2)). Selecting a
                            confusion matrix cell finally reveals the underlying{' '}
                            <span className="bold">multivariate time series data</span> to compare
                            correctly and wrongly predicted class representatives (Fig. 1 (3)) and to
                            verify or reject a hypothesis about the misclassification.
                        </p>
                    </Col>
                    <Col sm={1}></Col>
                </Row>
                <Row className="divider"></Row>
                <Row className="align-items-center" sm={12}>
                    <Col sm={1}></Col>
                    <Col>
                        <h2>Visual Analytics Workspace</h2>
                    </Col>
                    <Col sm={1}></Col>
                </Row>
                <Row className="divider"></Row>
                <Row className="align-items-center" sm={12}>
                    <Col sm={1}></Col>
                    <Col>
                        <img
                            className="images-text"
                            src="images/2020-debugging/use-case-targets.png"
                            alt="Demonstrator of the multi-target visual debugging workflow"
                        />
                    </Col>
                    <Col sm={1}></Col>
                </Row>
                <Row className="divider"></Row>
                <Row className="align-items-center" sm={12}>
                    <Col sm={1}></Col>
                    <Col>
                        <p>
                            The prototype consists of three interlinked views. The{' '}
                            <span className="bold">Multi-target Parallel Coordinate Plot</span> (B)
                            gives an entry-level overview based on parallel coordinates<sup>[1]</sup>{' '}
                            and parallel sets<sup>[2]</sup>: columns represent the classes of a
                            target, cell color encodes the misclassification error, and the
                            thickness of the links between columns represents the misclassification
                            of the relationship between targets, revealing partially correct results.
                        </p>
                        <p>
                            Selecting classes for n-1 of the n targets transitions to the{' '}
                            <span className="bold">Enhanced Confusion Matrix Visualization</span>{' '}
                            (C) for the remaining single target. Its cells are enhanced with{' '}
                            <span className="bold">
                                time series cell representatives
                            </span>{' '}
                            (e.g., a rolling median or engineered features) and colored by either the
                            misclassification error or the pair- and point-wise difference between a
                            misclassified sample and the correct or predicted class, supporting
                            hypothesis generation about decisive features. Hovering over a
                            misclassified cell highlights the true and predicted class
                            representatives (F) in green and red.
                        </p>
                        <p>
                            Selecting a cell transitions to the{' '}
                            <span className="bold">Comparative Time Series View</span> (D+E), which
                            compares Themeriver-based<sup>[3]</sup> quantile graphs of the true and
                            predicted classes to the underlying misclassified time series samples,
                            enabling the exploration of time spans in which the classifier struggles
                            and the identification of heterogeneous or corrupt sensor data (G).
                        </p>
                    </Col>
                    <Col sm={1}></Col>
                </Row>
                <Row className="divider"></Row>
                <Row className="align-items-center" sm={12}>
                    <Col sm={1}></Col>
                    <Col>
                        <h2>Usage Scenario: Predictive Maintenance</h2>
                    </Col>
                    <Col sm={1}></Col>
                </Row>
                <Row className="divider"></Row>
                <Row className="align-items-center" sm={12}>
                    <Col sm={1}></Col>
                    <Col>
                        <p>
                            We illustrate the workflow on an anonymized, real-world predictive
                            maintenance dataset from an industry partner, consisting of{' '}
                            <span className="bold">20 million time points with six sensor
                            features</span>, segmented and used to fine-tune a decision tree, a
                            random forest<sup>[4]</sup>, and a neural network classifier.
                        </p>
                        <ul>
                            <li>
                                In the <span className="bold">Multi-target Parallel Coordinate
                                Plot</span>, the decision tree classifies two targets well but shows
                                a high error and a partially correct result for classes of a third
                                target, marking it for further investigation.
                            </li>
                            <li>
                                In the <span className="bold">Enhanced Confusion Matrix</span>, using
                                the mean Fourier-transformation of each cell as the class
                                representative, the misclassified samples turn out to be more similar
                                to the predicted class representative than to the correct one,
                                generating a first hypothesis about a problematic feature.
                            </li>
                            <li>
                                In the <span className="bold">Comparative Time Series View</span>,
                                comparing the misclassified samples to the quantile graphs of the
                                true and predicted classes shows that two of the six sensor features
                                are shifted out of the expected distribution. One feature turns out
                                to be constant during the interval, revealing a{' '}
                                <span className="bold">sensor failure</span> as the root cause of the
                                misclassification, a finding verified by the domain expert.
                            </li>
                        </ul>
                    </Col>
                    <Col sm={1}></Col>
                </Row>
                <Row className="divider"></Row>
                <Row className="align-items-center">
                    <Col sm={1}></Col>
                    <Col>
                        <p className="references">
                            [1] Inselberg, A. (1985). The Plane with Parallel Coordinates. The
                            Visual Computer, 1(2), 69-91
                        </p>
                        <p className="references">
                            [2] Bendix, F., Kosara, R., & Hauser, H. (2005). Parallel Sets: Visual
                            Analysis of Categorical Data. IEEE Symposium on Information
                            Visualization
                        </p>
                        <p className="references">
                            [3] Havre, S., Hetzler, B., & Nowell, L. (2002). ThemeRiver: Visualizing
                            Theme Changes over Time. IEEE Symposium on Information Visualization
                        </p>
                        <p className="references">
                            [4] Breiman, L. (2001). Random Forests. Machine Learning, 45(1), 5-32
                        </p>
                    </Col>
                    <Col sm={1}></Col>
                </Row>
            </Container>
        </section>
    );
}
